import { useEffect, useMemo, useState } from 'react'
import { resolveCatalogProductPurchasability } from '../../purchasability'
import type { CatalogProduct, CatalogProductFieldDefinition } from '../../types'
import { ProductFieldValue } from '../product-page/ProductFieldValue'
import { resolveRendererForField } from '../product-page/field-renderers/registry'
import type { ProductCustomFieldRow } from '../product-page/helpers'
import {
  buildProductPath,
  formatPrice,
  getProductImageSources,
  resolveAddToCartLabel,
} from '../categoryPageHelpers'

type Props = {
  products: CatalogProduct[]
  trailSlugs: string[]
  toLocalizedPath: (path: string) => string
  cartBusy: boolean
  productFieldDefinitions: CatalogProductFieldDefinition[]
  onAddToCart: (product: CatalogProduct) => void
}

const normalizeProductType = (value: string | undefined) =>
  (value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

const formatFieldValue = (field: CatalogProductFieldDefinition, value: unknown): string => {
  const values = Array.isArray(value) ? value : [value]
  const formatted = values
    .map((entry) => {
      if (typeof entry === 'boolean') return entry ? 'Sì' : 'No'
      const raw = String(entry)
      return field.options.find((option) => option.value === raw)?.label || raw
    })
    .filter(Boolean)
    .join(', ')
  if (!formatted) return ''
  return field.unit ? `${formatted} ${field.unit}` : formatted
}

const resolveProductFieldValue = (product: CatalogProduct, key: string): unknown => {
  const parentValue = product.customFields?.[key]
  if (parentValue !== undefined && parentValue !== null && parentValue !== '') return parentValue

  const variantValues = (product.variants ?? [])
    .flatMap((variant) => [variant.attributes?.[key], variant.overrides?.[key]])
    .filter((value): value is NonNullable<typeof value> => value !== undefined && value !== null && value !== '')
  const uniqueValues = Array.from(new Set(variantValues.map((value) => JSON.stringify(value)))).map((value) => JSON.parse(value) as unknown)
  return uniqueValues.length === 1 ? uniqueValues[0] : uniqueValues.length > 1 ? uniqueValues : undefined
}

const resolveSummaryFields = (product: CatalogProduct, definitions: CatalogProductFieldDefinition[]) => {
  const productType = normalizeProductType(product.productType)
  return definitions
    .filter((field) => {
      const normalizedKey = field.key.toLowerCase().replace(/[\s_-]+/g, '')
      if (normalizedKey.includes('warranty') || normalizedKey.includes('garanzia')) return false
      return (!productType || field.productType === productType) &&
        (['summary', 'highlights'].includes(field.storefront.zone) || field.display?.showInSummary === true)
    })
    .sort((left, right) => (left.display?.order ?? left.storefront.order) - (right.display?.order ?? right.storefront.order))
    .map((field) => ({ field, value: resolveProductFieldValue(product, field.key) }))
    .filter(({ value }) => value !== undefined && value !== null && value !== '')
    .slice(0, 5)
}

const getLabelInitials = (label: string): string =>
  label
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

const ProductCard = ({
  product,
  trailSlugs,
  toLocalizedPath,
  cartBusy,
  productFieldDefinitions,
  onAddToCart,
}: {
  product: CatalogProduct
  trailSlugs: string[]
  toLocalizedPath: (path: string) => string
  cartBusy: boolean
  productFieldDefinitions: CatalogProductFieldDefinition[]
  onAddToCart: (product: CatalogProduct) => void
}) => {
  const productImage = getProductImageSources(
    product,
    '(min-width: 1280px) 30vw, (min-width: 768px) 46vw, 92vw',
  )
  const [imageUnavailable, setImageUnavailable] = useState(!productImage.src)

  useEffect(() => {
    setImageUnavailable(!productImage.src)
  }, [productImage.src])

  const productName = (product.name || product.slug || 'Prodotto').trim() || 'Prodotto'
  const price = formatPrice(product)
  const purchasability = resolveCatalogProductPurchasability(product)
  const productPath = buildProductPath(trailSlugs, product)
  const addLabel = resolveAddToCartLabel(product)
  const placeholderInitials = useMemo(() => getLabelInitials(productName), [productName])
  const summaryFields = useMemo(
    () => resolveSummaryFields(product, productFieldDefinitions),
    [product, productFieldDefinitions],
  )

  return (
    <article className="group shop-surface rounded-xl p-3 text-left transition hover:-translate-y-0.5 hover:shadow-lg sm:p-4">
      <a href={toLocalizedPath(productPath)} className="block">
        <div className="relative isolate aspect-[4/3] overflow-hidden rounded-lg">
          {imageUnavailable ? (
            <div className="absolute inset-0 grid place-items-center bg-slate-100 text-slate-500">
              <span className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold tracking-wide">
                {placeholderInitials || 'PR'}
              </span>
            </div>
          ) : (
            <img
              src={productImage.src}
              srcSet={productImage.srcSet}
              sizes={productImage.sizes}
              alt={productName}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              onError={() => setImageUnavailable(true)}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
            />
          )}
        </div>

        <div className="space-y-2 px-1 pb-2 pt-4">
          <p className="line-clamp-2 text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl">{productName}</p>
          <p className="line-clamp-2 text-sm leading-relaxed text-slate-600">
            {product.description?.trim() || 'Scopri dettagli e specifiche del prodotto.'}
          </p>
          {summaryFields.length ? (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-slate-100 pt-2 text-xs text-slate-600">
              {summaryFields.map(({ field, value }) => {
                const row: ProductCustomFieldRow = { field, value }
                const renderer = resolveRendererForField(field)
                return (
                <div key={field.id} className="min-w-0">
                  <dt className="truncate font-medium text-slate-500">{field.label}</dt>
                  <dd className="truncate font-semibold text-slate-800">
                    {renderer ? <ProductFieldValue row={row} productData={product} /> : formatFieldValue(field, value)}
                  </dd>
                </div>
                )
              })}
            </dl>
          ) : null}
        </div>
      </a>

      <div className="flex items-center justify-between gap-2 px-1 pb-1">
        {price ? (
          <p className="text-base font-semibold text-[color:var(--theme-color-primary,#047857)]">{price}</p>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => onAddToCart(product)}
          disabled={cartBusy || !purchasability.isPurchasable}
          className="rounded-full border border-emerald-300 bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {addLabel}
        </button>
      </div>
    </article>
  )
}

const CategoryProductsGrid = ({ products, trailSlugs, toLocalizedPath, cartBusy, productFieldDefinitions, onAddToCart }: Props) => (
  <div className="grid gap-4 sm:gap-5 md:grid-cols-2 xl:grid-cols-3">
    {products.map((product) => (
      <ProductCard
        key={product._id}
        product={product}
        trailSlugs={trailSlugs}
        toLocalizedPath={toLocalizedPath}
        cartBusy={cartBusy}
        productFieldDefinitions={productFieldDefinitions}
        onAddToCart={onAddToCart}
      />
    ))}
  </div>
)

export default CategoryProductsGrid
